import { cn } from "@istari-frontend/shared";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@istari-frontend/ui";
import { ChevronDown } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import { buildJsonPath } from "~/utils/jsonpath";

import { type JsonArray, type JsonObject, type JsonValue } from "../json-tree-viewer.utils";

interface JsonPrettyViewProps {
  data: JsonValue;
  collapsedPaths: ReadonlySet<string>;
  onToggleCollapsed: (path: string) => void;
  selectedPath?: string | null;
  onSelectPath?: (path: string) => void;
}

export function JsonPrettyView({
  data,
  collapsedPaths,
  onToggleCollapsed,
  selectedPath,
  onSelectPath,
}: JsonPrettyViewProps) {
  const context = useMemo(
    () => ({ collapsedPaths, onToggleCollapsed, selectedPath, onSelectPath }),
    [collapsedPaths, onToggleCollapsed, selectedPath, onSelectPath],
  );

  return (
    <JsonPrettyContext.Provider value={context}>
      <div className="p-2 text-sm">
        <JsonRoot data={data} />
      </div>
    </JsonPrettyContext.Provider>
  );
}

export function collectCollapsiblePaths(data: JsonValue): string[] {
  if (!isJsonContainer(data)) return [];
  return getEntries(data).flatMap((entry) => collectCollapsiblePathsForEntry(entry, [entry.key]));
}

/** Every collapsible path except the top-level ones: what stays folded when a document is large. */
export function collectNestedCollapsiblePaths(data: JsonValue): string[] {
  if (!isJsonContainer(data)) return [];
  return getEntries(data).flatMap((entry) =>
    isJsonContainer(entry.value)
      ? getEntries(entry.value).flatMap((child) => collectCollapsiblePathsForEntry(child, [entry.key, child.key]))
      : [],
  );
}

interface JsonEntry {
  key: string | number;
  value: JsonValue;
}

type EntryKind = "leaf" | "empty" | "inline-array" | "section";

interface JsonPrettyContextValue {
  collapsedPaths: ReadonlySet<string>;
  onToggleCollapsed: (path: string) => void;
  selectedPath?: string | null;
  onSelectPath?: (path: string) => void;
}

const JsonPrettyContext = createContext<JsonPrettyContextValue>({
  collapsedPaths: new Set<string>(),
  onToggleCollapsed: () => undefined,
});

const ROOT_PATH: (string | number)[] = [];

function JsonRoot({ data }: { data: JsonValue }) {
  if (!isJsonContainer(data)) {
    return (
      <div className="px-2 py-1">
        <JsonLeafValue value={data} />
      </div>
    );
  }

  const entries = getEntries(data);
  if (entries.length === 0) {
    return <p className="text-muted-foreground px-2 py-1 text-xs">{describeEmpty(data)}</p>;
  }

  return <JsonEntryList entries={entries} path={ROOT_PATH} />;
}

function JsonEntryList({ entries, path }: { entries: JsonEntry[]; path: (string | number)[] }) {
  return (
    <div className="flex flex-col">
      {entries.map((entry) => (
        <JsonEntryRow key={String(entry.key)} entry={entry} path={[...path, entry.key]} />
      ))}
    </div>
  );
}

function JsonEntryRow({ entry, path }: { entry: JsonEntry; path: (string | number)[] }) {
  if (getEntryKind(entry.value) === "section") {
    return <JsonSection entry={entry} path={path} />;
  }
  return <JsonFieldRow entry={entry} path={path} />;
}

function JsonSection({ entry, path }: { entry: JsonEntry; path: (string | number)[] }) {
  const { collapsedPaths, onToggleCollapsed } = useContext(JsonPrettyContext);
  const jsonPath = buildJsonPath(path);
  const open = !collapsedPaths.has(jsonPath);
  const entries = isJsonContainer(entry.value) ? getEntries(entry.value) : [];

  return (
    <Collapsible open={open} onOpenChange={() => onToggleCollapsed(jsonPath)}>
      <CollapsibleTrigger className="hover:bg-accent/50 focus-visible:ring-ring flex w-full items-center gap-1.5 rounded px-2 py-1 text-left transition-colors focus-visible:ring-1 focus-visible:outline-none">
        <ChevronDown
          className={cn("text-muted-foreground size-3.5 shrink-0 transition-transform", !open && "-rotate-90")}
        />
        <span className="text-foreground truncate text-xs font-semibold">{formatKey(entry.key)}</span>
        <span className="text-muted-foreground shrink-0 text-xs">
          {summarizeContainer(entry.value, entries.length)}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-border ml-[0.6875rem] border-l pl-2">
          <JsonEntryList entries={entries} path={path} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function JsonFieldRow({ entry, path }: { entry: JsonEntry; path: (string | number)[] }) {
  const { selectedPath, onSelectPath } = useContext(JsonPrettyContext);
  const jsonPath = buildJsonPath(path);
  const selected = selectedPath === jsonPath;
  const rowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  return (
    <button
      ref={rowRef}
      type="button"
      onClick={() => onSelectPath?.(jsonPath)}
      className={cn(
        "hover:bg-accent/50 focus-visible:ring-ring grid w-full grid-cols-[minmax(0,11rem)_minmax(0,1fr)] items-baseline gap-3 rounded px-2 py-1 text-left transition-colors focus-visible:ring-1 focus-visible:outline-none",
        selected && "bg-accent",
      )}
    >
      <span className="text-muted-foreground truncate text-xs font-medium">{formatKey(entry.key)}</span>
      <JsonFieldValue value={entry.value} />
    </button>
  );
}

function JsonFieldValue({ value }: { value: JsonValue }) {
  const kind = getEntryKind(value);

  if (kind === "empty") {
    return <span className="text-muted-foreground text-xs">{describeEmpty(value)}</span>;
  }

  if (kind === "inline-array") {
    return (
      <span className="flex flex-wrap items-baseline gap-1">
        {(value as JsonArray).map((item, index) => (
          <span key={buildJsonPath([index])} className="bg-muted rounded px-1.5 py-0.5 text-xs">
            <JsonLeafValue value={item} />
          </span>
        ))}
      </span>
    );
  }

  return <JsonLeafValue value={value} />;
}

function JsonLeafValue({ value }: { value: JsonValue }) {
  if (value === null) return <span className="text-muted-foreground italic">null</span>;
  if (typeof value === "boolean") return <span className="text-foreground font-medium">{String(value)}</span>;
  if (typeof value === "number") return <span className="text-foreground tabular-nums">{value}</span>;
  if (value === "") return <span className="text-muted-foreground italic">empty</span>;
  return <span className="text-foreground break-words whitespace-pre-wrap">{String(value)}</span>;
}

function collectCollapsiblePathsForEntry(entry: JsonEntry, path: (string | number)[]): string[] {
  if (getEntryKind(entry.value) !== "section" || !isJsonContainer(entry.value)) return [];
  return [
    buildJsonPath(path),
    ...getEntries(entry.value).flatMap((child) => collectCollapsiblePathsForEntry(child, [...path, child.key])),
  ];
}

function getEntryKind(value: JsonValue): EntryKind {
  if (!isJsonContainer(value)) return "leaf";
  const entries = getEntries(value);
  if (entries.length === 0) return "empty";
  if (Array.isArray(value) && entries.every((entry) => !isJsonContainer(entry.value))) return "inline-array";
  return "section";
}

function getEntries(value: JsonObject | JsonArray): JsonEntry[] {
  if (Array.isArray(value)) {
    return value.map((item, index) => ({ key: index, value: item }));
  }
  return Object.entries(value).map(([key, entryValue]) => ({ key, value: entryValue }));
}

function isJsonContainer(value: JsonValue): value is JsonObject | JsonArray {
  return typeof value === "object" && value !== null;
}

function formatKey(key: string | number): string {
  return typeof key === "number" ? `[${key}]` : key;
}

function summarizeContainer(value: JsonValue, count: number): string {
  const noun = Array.isArray(value) ? "item" : "field";
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function describeEmpty(value: JsonValue): string {
  return Array.isArray(value) ? "Empty array" : "Empty object";
}
